import { useQuery } from "@tanstack/react-query";
import { ProfileApiService } from "@Api/profile-service";
import { queryKeys } from "@Constants/queryKeys";
import useAuthStore from "@Store/authStore";

const getTextValue = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const getAvatarUrl = (...values: unknown[]) => {
  for (const value of values) {
    if (!value) {
      continue;
    }

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }

    if (typeof value === "object") {
      const media = value as Record<string, unknown>;
      const mediaUrl = getTextValue(
        media.blobViewableUrl,
        media.thumbnailViewableUrl,
        media.viewableUrl,
        media.url,
        media.uri,
        media.path
      );

      if (mediaUrl) {
        return mediaUrl;
      }
    }
  }

  return "";
};

export type NormalizedProfile = {
  id: string;
  userName: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  avatarUrl: string;
  raw: Record<string, any>;
};

export const useProfileDetailsQuery = () => {
  const userData = useAuthStore((state) => state.userData);

  return useQuery({
    queryKey: [queryKeys.userManagement.profile],
    queryFn: async (): Promise<NormalizedProfile> => {
      const response = await ProfileApiService.getProfileDetails();

      if (!response.ok) {
        const message =
          response?.response?.message || "Failed to fetch profile details.";
        throw new Error(message);
      }

      const payload = response?.data;
      const profileData =
        // PUT/GET /personal/profile return HttpResponseDto<UserDetailsDto>: the DTO is `body`.
        payload?.data?.body ??
        payload?.data ??
        payload?.body?.data ??
        {};
      const firstName = getTextValue(
        profileData?.firstName,
        payload?.firstName,
        userData?.firstName,
        userData?.user?.firstName
      );
      const lastName = getTextValue(
        profileData?.lastName,
        payload?.lastName,
        userData?.lastName,
        userData?.user?.lastName
      );
      const fullName =
        `${firstName} ${lastName}`.trim() ||
        getTextValue(
          profileData?.fullName,
          payload?.fullName,
          profileData?.name,
          payload?.name,
          userData?.fullName,
          userData?.name
        );

      const normalizedProfile = {
        id: getTextValue(
          profileData?.id,
          payload?.id,
          profileData?.Id,
          userData?.id,
          userData?.user?.id
        ),
        userName: getTextValue(
          profileData?.userName,
          payload?.userName,
          userData?.userName,
          userData?.email
        ),
        firstName,
        lastName,
        fullName,
        email: getTextValue(
          profileData?.email,
          payload?.email,
          userData?.email,
          userData?.user?.email,
          profileData?.userName,
          payload?.userName
        ),
        imageUrl: getAvatarUrl(
          profileData?.imageUrl,
          payload?.imageUrl,
          profileData?.profileImage,
          profileData?.profilePicture,
          profileData?.profilePic,
          profileData?.image,
          profileData?.avatar,
          userData?.imageUrl,
          userData?.image
        ),
      };

      return {
        id: normalizedProfile.id,
        userName: normalizedProfile.userName,
        firstName: normalizedProfile.firstName,
        lastName: normalizedProfile.lastName,
        fullName: normalizedProfile.fullName,
        email: normalizedProfile.email,
        avatarUrl: normalizedProfile.imageUrl,
        raw: profileData,
      };
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: "always",
    retry: 1,
  });
};